/**
 * Shared error-formatter for admin-side toasts.
 *
 * The pre-May-2026 pattern across the admin pages was:
 *   toast.error(err.response?.data?.detail || "Something failed")
 *
 * That breaks the moment the backend returns a structured detail (object
 * with .code / .message / .hint — e.g. the 409 pending_registration_exists
 * flow). In those cases the user saw the generic fallback and the real
 * error was invisible.
 *
 * This helper unpacks:
 *   1. string detail          → use as-is
 *   2. object detail.message  → surface the human-readable message
 *   3. HTTP status + statusText (when backend returned no body)
 *   4. network/axios error.message
 *   5. fallback (dev-provided default)
 *
 * Use the returned object with toast.error(msg, opts) — opts includes a
 * 12-second duration so admins can actually read the message.
 *
 * Usage:
 *   import { formatAdminError } from "../lib/adminError";
 *   ...
 *   } catch (err) {
 *     const { message, opts } = formatAdminError(err, "Failed to load X");
 *     toast.error(message, opts);
 *   }
 */
export function formatAdminError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  let message;

  if (typeof detail === "string" && detail.trim()) {
    message = detail;
  } else if (detail && typeof detail === "object") {
    // Structured detail (object). Prefer .message, then .hint, then stringify.
    message = detail.message || detail.hint || detail.code || JSON.stringify(detail);
  } else if (err?.response?.status) {
    const statusText = err.response.statusText || "server error";
    message = `${fallback} (HTTP ${err.response.status}: ${statusText})`;
  } else if (err?.message) {
    message = `${fallback}: ${err.message}`;
  } else {
    message = fallback;
  }

  return {
    message,
    opts: { duration: 12000 },
  };
}
